/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2022, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import _ from 'lodash';

import {
  CONVERSATIONAL_SEARCH_DATA_TYPE,
  CONVERSATIONAL_SEARCH_VARIABLE_PATH
} from '../../../ts/action';
import { VariableOperand } from '../../../ts/operand';

export const ROWS_PER_PAGE_DEFAULT = 50;
export const ROWS_PER_PAGE_OPTIONS = [25, 50, 75, 100];

export const AVAILABLE_SYSTEM_VARIABLES = [
  'channel_name',
  'current_time',
  'current_date',
  'now',
  'last_seen_at',
  'locale',
  'timezone',
  'digressed_from',
  'fallback_reason',
  'no_action_matches_count',
  'session_history'
];

export const isSystemVariable = (varId: string) =>
  AVAILABLE_SYSTEM_VARIABLES.indexOf(varId) !== -1;

export const DATA_TYPE_RESPONSE_TYPES = {
  option: { id: 'option', name: 'Options' },
  regex: { id: 'regex', name: 'Regex' },
  dynamic_option: { id: 'dynamic-option', name: 'Dynamic options' }
};

export const ENTITY_TYPES = {
  channel_name: {
    id: 'sys-synthetic_channel_name_entity',
    name: 'Channel name'
  },
  confirmation: { id: 'sys-yes-no', name: 'Confirmation' },
  currency: { id: 'sys-currency', name: 'Currency' },
  data_type: { id: 'data_type', name: 'Data Type' },
  date: { id: 'sys-date', name: 'Date' },
  date_time: { id: 'sys-date-time', name: 'Date/Time' },
  fallback_reason: {
    id: 'sys-synthetic_fallback_reason_entity',
    name: 'Fallback reason'
  },
  free_text: { id: 'sys-free-text', name: 'Free text' },
  last_seen_at: {
    id: 'sys-synthetic_last_seen_at_entity',
    name: 'Current page url'
  },
  number: { id: 'sys-number', name: 'Number' },
  percent: { id: 'sys-percentage', name: 'Percent' },
  time: { id: 'sys-time', name: 'Time' },
  ...DATA_TYPE_RESPONSE_TYPES
};

export const TIME_VARS = {
  now: { id: 'now', name: 'Now' },
  current_date: { id: 'current_date', name: "User's local date" },
  current_time: { id: 'current_time', name: "User's local time" }
};

export const VARIABLE_CATEGORIES = {
  ASSISTANT: 'assistant',
  GLOBAL: 'global',
  INTEGRATION: 'integration',
  OPEN_API_EXTENSION: 'open_api_extension',
  STEP: 'step',
  SUBACTION: 'subaction',
  SEARCH: 'search'
};

export const CUSTOM_CATEGORY = 'Custom category';

export const VARIABLE_REGEXP = new RegExp('(\\$\\{.*?\\})', 'g');

export const TIMESTAMP_FORMATS = {
  DEFAULT: 'YYYY-MM-DDTHH:mmZ'
};

export const generateComplexVariableId = (variable, path) =>
  `${variable}.${path}`;

export const getVariableIdFromVariableValue = (value, variables?) => {
  if (!value) {
    return null;
  }
  if (
    value.variable &&
    variables &&
    value.child_property === value.variable &&
    variables[value.variable]?.convoSkillVariable
  ) {
    return value.variable;
  }
  if (value.child_property || value.variable_path) {
    return generateComplexVariableId(
      value.variable,
      value.child_property || value.variable_path
    );
  }
  return (
    value.variable || value.skill_variable || value.system_variable || null
  );
};

export const CONVERSATIONAL_SEARCH_VARIABLES = {
  conversational_search_status: {
    id: CONVERSATIONAL_SEARCH_VARIABLE_PATH.CONVERSATIONAL_SEARCH_STATUS,
    data_type: CONVERSATIONAL_SEARCH_DATA_TYPE.CONVERSATIONAL_SEARCH_STATUS,
    path: CONVERSATIONAL_SEARCH_VARIABLE_PATH.CONVERSATIONAL_SEARCH_STATUS,
    property_type: 'success',
    title: 'Conversational search status',
    description: 'Conversational search status'
  },
  conversational_search_response: {
    id: CONVERSATIONAL_SEARCH_VARIABLE_PATH.CONVERSATIONAL_SEARCH_RESPONSE,
    data_type: CONVERSATIONAL_SEARCH_DATA_TYPE.CONVERSATIONAL_SEARCH_RESPONSE,
    path: CONVERSATIONAL_SEARCH_VARIABLE_PATH.CONVERSATIONAL_SEARCH_RESPONSE,
    property_type: 'success',
    title: 'Conversational search response',
    description: 'Conversational search response'
  },
  citations: {
    id: CONVERSATIONAL_SEARCH_VARIABLE_PATH.CONVERSATIONAL_SEARCH_CITATIONS,
    data_type: CONVERSATIONAL_SEARCH_DATA_TYPE.CONVERSATIONAL_SEARCH_CITATIONS,
    path: CONVERSATIONAL_SEARCH_VARIABLE_PATH.CONVERSATIONAL_SEARCH_CITATIONS,
    property_type: 'success',
    title: 'Citations',
    description: 'Conversational search citations'
  }
};

export const CONVERSATIONAL_SEARCH_STATUS_VALUES = {
  generated_and_above_confidence_threshold:
    'Generated and above confidence threshold',
  generated_and_below_confidence_threshold:
    'Generated and below confidence threshold',
  failed_to_generate_no_search_results:
    'Failed to generate - no search results',
  failed_to_generate_below_confidence_threshold:
    'Failed to generate - search result confidence too low',
  failed_to_generate_error:
    'Failed to generate - unable to connect to search system or got error'
};

export const generatePropertiesForConversationalSearchVariable = () => {
  const properties = [
    CONVERSATIONAL_SEARCH_VARIABLES.conversational_search_status,
    CONVERSATIONAL_SEARCH_VARIABLES.conversational_search_response,
    CONVERSATIONAL_SEARCH_VARIABLES.citations
  ];
  return properties;
};

export const isCustomVariableCategory = variableCategory =>
  variableCategory?.category === CUSTOM_CATEGORY;

//---------------- Step variable ----------------
export const isStepVariable = variable =>
  variable?.categoryId === VARIABLE_CATEGORIES.STEP;
//---------------- Step variable ----------------

//---------------- Subaction variable ----------------
export const isSubactionVariable = variable =>
  variable?.categoryId === VARIABLE_CATEGORIES.SUBACTION;

export const parseSubActionVariableFromId = variableId => {
  if (variableId) {
    const parts = variableId.split('.');
    if (parts.length === 2) {
      return { variable: parts[0], child_property: parts[1] };
    }
  }
  return null;
};
//---------------- Subaction variable ----------------

//---------------- Open Api extenstion variable ----------------
export const isOpenApiExtensionVariable = variable =>
  variable?.categoryId === VARIABLE_CATEGORIES.OPEN_API_EXTENSION;

//---------------- Open Api extenstion variable ----------------

//---------------- Conversational search variable ----------------
export const isConversationalSearchVariable = variable =>
  variable?.categoryId === VARIABLE_CATEGORIES.SEARCH;
//---------------- Conversational search variable ----------------

//---------------- Session variable ----------------
export const isSessionVariable = variable =>
  variable?.categoryId === VARIABLE_CATEGORIES.GLOBAL;
//---------------- Session variable ----------------

//---------------- Assistant variable ----------------
export const isAssistantVariable = variable =>
  variable?.categoryId === VARIABLE_CATEGORIES.ASSISTANT;
//---------------- Assistant variable ----------------

//---------------- Integration variable ----------------
export const isIntegrationVariable = variable =>
  variable?.categoryId === VARIABLE_CATEGORIES.INTEGRATION;
//---------------- Integration variable ----------------

//---------------- Static variable ----------------
export const isStaticVariable = variable =>
  variable?.time || !_.isUndefined(variable?.scalar);
//---------------- Static variable ----------------

export const isLocalVariable = variable =>
  !isSessionVariable(variable) &&
  !isIntegrationVariable(variable) &&
  !isAssistantVariable(variable);

export const getVariableObject = (allVariables, variableId) => {
  if (allVariables && variableId) {
    let variableObject = allVariables[variableId];
    if (!variableObject && variableId.toLowerCase().startsWith('system_')) {
      const systemVarName = variableId.substring('system_'.length);
      variableObject = allVariables[systemVarName];
      if (!variableObject) {
        //there are some "hidden" system variables (such as "system_integrations") as a result
        //we will treat any variable whose prefix is "system_" as a valid variable.
        const tmpName =
          systemVarName.charAt(0).toUpperCase() + systemVarName.slice(1);
        variableObject = {
          description: tmpName,
          title: tmpName,
          variable: systemVarName
        };
      }
    }
    if (!variableObject && variableId.indexOf('.') !== -1) {
      const variableName = variableId.split('.').shift();
      variableObject = allVariables[variableName];
      if (variableObject) {
        const title =
          variableObject?.title?.length > 0
            ? variableObject.title
            : _.drop(variableId.split('.')).join('.');
        variableObject = { ...variableObject, title };
      }
    }
    if (variableObject) {
      return variableObject;
    }
  }
  return null;
};

export const getResultVariableId = resolver => {
  if (resolver?.callout) {
    return resolver.callout.result_variable;
  }
  if (resolver?.invoke_action) {
    return resolver.invoke_action.result_variable;
  }
  if (resolver?.client_actions) {
    return _.get(resolver, 'client_actions[0].result_variable');
  }
  if (resolver?.response?.after_generation?.result_variable) {
    return resolver?.response?.after_generation?.result_variable;
  }
  return null;
};

/**
 * Returns true if the action starts with `sys-synthetic`.
 */
export const isSyntheticAction = action =>
  action?.action?.startsWith('sys-synthetic_');

export const areVariableOperandsEqual = (
  a: VariableOperand,
  b: VariableOperand
) => {
  let result =
    a.child_property === b.child_property &&
    a.variable_path === b.variable_path;
  if ('variable' in a && 'variable' in b) {
    result = a.variable === b.variable && result;
  } else if ('skill_variable' in a && 'skill_variable' in b) {
    result = a.skill_variable === b.skill_variable && result;
  } else if ('system_variable' in a && 'system_variable' in b) {
    result = a.system_variable === b.system_variable && result;
  } else {
    result = false;
  }
  return result;
};

export const createVariableBlock = varObj => {
  let key = 'variable';
  if (isSessionVariable(varObj)) {
    key = 'skill_variable';
  } else if (isIntegrationVariable(varObj) || isAssistantVariable(varObj)) {
    key = 'system_variable';
  }
  if (varObj.stepVariable) {
    return { [key]: varObj.source, child_property: varObj.stepVariable };
  } else if (varObj.path) {
    // the response data is saved under the "body" property so we need to prefix "body" to the path
    return { [key]: varObj.source, variable_path: varObj.path };
  } else {
    return { [key]: varObj.variable };
  }
};

export const createSetVariableBlock = variableObject => {
  if (variableObject === undefined) {
    return { skill_variable: null };
  }
  if (
    variableObject?.expression ||
    variableObject?.time ||
    !_.isUndefined(variableObject?.scalar)
  ) {
    return variableObject;
  }
  if (variableObject?.from_entity) {
    return {
      scalar: variableObject?.title
    };
  }
  return createVariableBlock(variableObject);
};
